import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { SelectOption } from '@routes/profile-bar/___pages___/my-profile/ui/submitted-changes-list-tab/helper.tsx';
import { getAdminCenterData } from '@routes/sidebar/___shared___/model/adminCenterData.slice.ts';
import type {
    DuplicateRankSetResponse,
    ProficiencyLevelResponse,
    RankReferenceCommon,
    RankReferenceRequest,
    RankRequest,
    RankResponse,
    RankSetCreateRequest,
    RankSetResponse,
    RankShortCodeResponse,
    UpdateRankSetRequest,
    VesselDepartmentResponse,
} from '@routes/sidebar/admin-center/___shared___/api/rankReference.api.ts';
import {
    createApiRankReferences,
    createApiRankSet,
    deleteApiRankSet,
    duplicateApiRankSet,
    getApiProficiencyLevels,
    getApiRankReferences,
    getApiRankSets,
    getApiRankShortCodes,
    getApiVesselDepartments,
    toggleActiveApiRankReferences,
    updateApiRankReferences,
    updateApiRankSet,
} from '@routes/sidebar/admin-center/___shared___/api/rankReference.api.ts';
import { REFERENCES_STEP_TITLE } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/constants.ts';
import { shouldSortByItemOrder } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/model/helpers.ts';
import { setPublishedSteps } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/model/configuration.slice.ts';
import type { TransformedSubItemForMatrices } from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/___shared___/components/custom-category-selector/types.ts';
import type { EmptyResultsDescription, PaginatedResponse } from '@types';
import { extractError } from '@utils/slice.ts';

export interface ReferencesError {
    message?: string;
    [k: string]: any;
}

export interface ReferencesState {
    error: ReferencesError | null;

    transformedRanksForMatrices: TransformedSubItemForMatrices[];

    ranks: RankResponse[] | null;
    commonRanks: PaginatedResponse<RankResponse, EmptyResultsDescription> | null;
    isGetRankReferencesPending: boolean;
    isUpsertRankReferencesPending: boolean;

    vesselDepartments: VesselDepartmentResponse[] | null;
    isGetVesselDepartmentsPending: boolean;

    proficiencyLevels: ProficiencyLevelResponse[] | null;
    isGetProficiencyLevelsPending: boolean;

    rankShortCodes: RankShortCodeResponse[] | null;
    isGetRankShortCodesPending: boolean;

    rankSets: RankSetResponse[] | null;
    currentGetRequestId: string | null;

    isGetRankSetsPending: boolean;
    isCreateRankSetPending: boolean;
    isUpdateRankSetPending: boolean;
    isDeleteRankSetPending: boolean;
    isDuplicateRankSetPending: boolean;

    isToggleActiveRankReferencePending: boolean;

    isAddNewRankSetModalOpened: boolean;
    isCopyRankSetModalOpened: boolean;
    isDeleteRankSetModalOpened: boolean;
    selectedRankSet: SelectOption | null;
    recordsRowOrder: string[];
}

const initialState: ReferencesState = {
    error: null,

    transformedRanksForMatrices: [],

    ranks: null,
    commonRanks: null,
    isGetRankReferencesPending: false,
    isUpsertRankReferencesPending: false,

    vesselDepartments: null,
    isGetVesselDepartmentsPending: false,

    proficiencyLevels: null,
    isGetProficiencyLevelsPending: false,

    rankShortCodes: null,
    isGetRankShortCodesPending: false,

    rankSets: null,
    currentGetRequestId: null,

    isGetRankSetsPending: false,
    isCreateRankSetPending: false,
    isUpdateRankSetPending: false,
    isDeleteRankSetPending: false,
    isDuplicateRankSetPending: false,

    isToggleActiveRankReferencePending: false,

    isAddNewRankSetModalOpened: false,
    isCopyRankSetModalOpened: false,
    isDeleteRankSetModalOpened: false,
    selectedRankSet: null,
    recordsRowOrder: [],
};

export const getRankReferences = createAsyncThunk<
    PaginatedResponse<RankResponse, EmptyResultsDescription>,
    { clientId: number; tenantAlias: string; rankSetId: number; payload: RankReferenceRequest },
    { rejectValue: ReferencesError }
>('adminCenterReferences/getRankReferences', ({ clientId, tenantAlias, rankSetId, payload }, { rejectWithValue }) =>
    getApiRankReferences(clientId, tenantAlias, rankSetId, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const upsertRankReference = createAsyncThunk<
    PaginatedResponse<RankResponse, EmptyResultsDescription>,
    { clientId: number; tenantAlias: string; rankSetId: number; payload: RankRequest[] },
    { rejectValue: ReferencesError }
>(
    'adminCenterReferences/updateApiRankReferences',
    ({ clientId, tenantAlias, rankSetId, payload }, { rejectWithValue, dispatch }) =>
        updateApiRankReferences(clientId, tenantAlias, rankSetId, payload)
            .then((res) => {
                dispatch(setPublishedSteps(REFERENCES_STEP_TITLE));
                return res;
            })
            .catch((err) => rejectWithValue(err)),
);

export const createRankReference = createAsyncThunk<
    PaginatedResponse<RankResponse, EmptyResultsDescription>,
    { clientId: number; tenantAlias: string; rankSetId: number; payload: RankReferenceCommon },
    { rejectValue: ReferencesError }
>(
    'adminCenterReferences/createApiRankReferences',
    ({ clientId, tenantAlias, rankSetId, payload }, { rejectWithValue, dispatch }) =>
        createApiRankReferences(clientId, tenantAlias, rankSetId, payload)
            .then((res) => {
                dispatch(setPublishedSteps(REFERENCES_STEP_TITLE));
                return res;
            })
            .catch((err) => rejectWithValue(err)),
);

export const toggleActiveRankReference = createAsyncThunk<
    PaginatedResponse<RankResponse, EmptyResultsDescription>,
    { clientId: number; tenantAlias: string; rankSetId: number; rankReferenceId: number },
    { rejectValue: ReferencesError }
>(
    'adminCenterReferences/toggleActiveRankReference',
    ({ clientId, tenantAlias, rankSetId, rankReferenceId }, { rejectWithValue }) =>
        toggleActiveApiRankReferences(clientId, tenantAlias, rankSetId, rankReferenceId)
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

export const getVesselDepartments = createAsyncThunk<
    PaginatedResponse<VesselDepartmentResponse, EmptyResultsDescription>,
    { clientId: number; tenantAlias: string },
    { rejectValue: ReferencesError }
>('adminCenterReferences/getVesselDepartments', ({ clientId, tenantAlias }, { rejectWithValue }) =>
    getApiVesselDepartments(clientId, tenantAlias)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getProficiencyLevels = createAsyncThunk<
    PaginatedResponse<ProficiencyLevelResponse, EmptyResultsDescription>,
    { clientId: number; tenantAlias: string },
    { rejectValue: ReferencesError }
>('adminCenterReferences/getProficiencyLevels', ({ clientId, tenantAlias }, { rejectWithValue }) =>
    getApiProficiencyLevels(clientId, tenantAlias)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getRankShortCodes = createAsyncThunk<
    RankShortCodeResponse[],
    { clientId: number; tenantAlias: string },
    { rejectValue: ReferencesError }
>('adminCenterReferences/getRankShortCodes', ({ clientId, tenantAlias }, { rejectWithValue }) =>
    getApiRankShortCodes(clientId, tenantAlias)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getRankSets = createAsyncThunk<
    RankSetResponse[],
    { clientId: number; tenantAlias: string },
    { rejectValue: ReferencesError }
>('adminCenterReferences/getRankSets', ({ clientId, tenantAlias }, { rejectWithValue }) =>
    getApiRankSets(clientId, tenantAlias)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const createRankSet = createAsyncThunk<
    RankSetResponse[],
    { clientId: number; tenantAlias: string; payload: RankSetCreateRequest },
    { rejectValue: ReferencesError }
>('adminCenterReferences/createRankSet', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    createApiRankSet(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateRankSet = createAsyncThunk<
    RankSetResponse[],
    { clientId: number; tenantAlias: string; id: number; payload: UpdateRankSetRequest },
    { rejectValue: ReferencesError }
>('adminCenterReferences/updateRankSet', ({ clientId, tenantAlias, id, payload }, { rejectWithValue }) =>
    updateApiRankSet(clientId, tenantAlias, id, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const deleteRankSet = createAsyncThunk<
    number,
    { clientId: number; tenantAlias: string; id: number },
    { rejectValue: ReferencesError }
>('adminCenterReferences/deleteRankSet', ({ clientId, tenantAlias, id }, { rejectWithValue }) =>
    deleteApiRankSet(clientId, tenantAlias, id)
        .then(() => id)
        .catch((err) => rejectWithValue(err)),
);

export const duplicateRankSet = createAsyncThunk<
    RankSetResponse,
    { clientId: number; tenantAlias: string; payload: DuplicateRankSetResponse },
    { rejectValue: ReferencesError }
>('adminCenterReferences/duplicateRankSet', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    duplicateApiRankSet(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const rankReferenceSlice = createSlice({
    name: 'adminCenterReferences',
    initialState,
    reducers: {
        setIsAddNewRankSetModalOpened(state, action: PayloadAction<boolean>) {
            state.isAddNewRankSetModalOpened = action.payload;
        },
        setIsCopyRankSetModalOpened(state, action: PayloadAction<boolean>) {
            state.isCopyRankSetModalOpened = action.payload;
        },
        setIsDeleteRankSetModalOpened(state, action: PayloadAction<boolean>) {
            state.isDeleteRankSetModalOpened = action.payload;
        },
        setSelectedRankSet(state, action: PayloadAction<SelectOption | null>) {
            const next = action.payload;

            if (state.selectedRankSet?.value === next?.value && state.selectedRankSet?.label === next?.label) {
                return;
            }

            state.selectedRankSet = next;
        },

        setRecordsRowOrder: (state, action: PayloadAction<string[]>) => {
            state.recordsRowOrder = action.payload;
        },

        resetRecordsRowOrder: (state) => {
            state.recordsRowOrder = [];
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getRankReferences.pending, (state, action) => {
            state.isGetRankReferencesPending = true;
            state.error = null;
            state.currentGetRequestId = action.meta.requestId;
        });
        builder.addCase(getRankReferences.fulfilled, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetRankReferencesPending = false;
            state.currentGetRequestId = null;

            const reqPayload = action.meta.arg.payload;
            const data = action.payload?.data ?? state.ranks;

            state.ranks = data;

            if (shouldSortByItemOrder(reqPayload) || !state.commonRanks) {
                state.commonRanks = action.payload;
            }

            // for matrices rank selector
            state.transformedRanksForMatrices = data
                // filter out ranks without department or proficiency level and inactive ranks
                .filter((rank) => rank.vesselDepartmentId !== null && rank.proficiencyLevelId !== null)
                .filter((item) => item.isActive)
                .map((rank) => ({
                    value: String(rank.id),
                    label: rank.tenantShortCode,
                    type: String(rank.vesselDepartmentId),
                    category: String(rank.proficiencyLevelId),
                    itemOrder: rank.itemOrder,
                }));
        });
        builder.addCase(getRankReferences.rejected, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetRankReferencesPending = false;
            state.currentGetRequestId = null;
            state.error = extractError(action);
        });

        builder.addCase(upsertRankReference.pending, (state) => {
            state.isUpsertRankReferencesPending = true;
            state.error = null;
        });
        builder.addCase(upsertRankReference.fulfilled, (state, action) => {
            state.isUpsertRankReferencesPending = false;
            state.ranks = action.payload?.data ?? state.ranks;
        });
        builder.addCase(upsertRankReference.rejected, (state, action) => {
            state.isUpsertRankReferencesPending = false;
            state.error = extractError(action);
        });

        builder.addCase(createRankReference.pending, (state) => {
            state.isUpsertRankReferencesPending = true;
            state.error = null;
        });
        builder.addCase(createRankReference.fulfilled, (state, action) => {
            state.isUpsertRankReferencesPending = false;

            state.ranks = action.payload?.data ?? state.ranks;
        });
        builder.addCase(createRankReference.rejected, (state, action) => {
            state.isUpsertRankReferencesPending = false;
            state.error = extractError(action);
        });

        /* vessel departments */
        builder.addCase(getVesselDepartments.pending, (state) => {
            state.isGetVesselDepartmentsPending = true;
            state.error = null;
        });
        builder.addCase(getVesselDepartments.fulfilled, (state, action) => {
            state.isGetVesselDepartmentsPending = false;
            state.vesselDepartments = action.payload.data ?? [];
        });
        builder.addCase(getVesselDepartments.rejected, (state, action) => {
            state.isGetVesselDepartmentsPending = false;
            state.error = extractError(action);
        });

        /* proficiency levels */
        builder.addCase(getProficiencyLevels.pending, (state) => {
            state.isGetProficiencyLevelsPending = true;
            state.error = null;
        });
        builder.addCase(getProficiencyLevels.fulfilled, (state, action) => {
            state.isGetProficiencyLevelsPending = false;
            state.proficiencyLevels = action.payload.data ?? [];
        });
        builder.addCase(getProficiencyLevels.rejected, (state, action) => {
            state.isGetProficiencyLevelsPending = false;
            state.error = extractError(action);
        });

        /* rank short codes */
        builder.addCase(getRankShortCodes.pending, (state) => {
            state.isGetRankShortCodesPending = true;
            state.error = null;
        });
        builder.addCase(getRankShortCodes.fulfilled, (state, action) => {
            state.isGetRankShortCodesPending = false;
            state.rankShortCodes = action.payload ?? [];
        });
        builder.addCase(getRankShortCodes.rejected, (state, action) => {
            state.isGetRankShortCodesPending = false;
            state.error = extractError(action);
        });

        /* rank sets */
        builder.addCase(getRankSets.pending, (state) => {
            state.isGetRankSetsPending = true;
            state.error = null;
        });
        builder.addCase(getRankSets.fulfilled, (state, action) => {
            state.isGetRankSetsPending = false;
            state.rankSets = action.payload ?? [];
        });
        builder.addCase(getRankSets.rejected, (state, action) => {
            state.isGetRankSetsPending = false;
            state.error = extractError(action);
            state.rankSets = []; // Clear rank sets on error to prevent infinite loading in dropdown
            state.selectedRankSet = null; // Clear selected rank set on error
        });

        builder.addCase(createRankSet.pending, (state) => {
            state.isCreateRankSetPending = true;
            state.error = null;
        });
        builder.addCase(createRankSet.fulfilled, (state) => {
            state.isCreateRankSetPending = false;
        });
        builder.addCase(createRankSet.rejected, (state, action) => {
            state.isCreateRankSetPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateRankSet.pending, (state) => {
            state.isUpdateRankSetPending = true;
            state.error = null;
        });
        builder.addCase(updateRankSet.fulfilled, (state) => {
            state.isUpdateRankSetPending = false;
        });
        builder.addCase(updateRankSet.rejected, (state, action) => {
            state.isUpdateRankSetPending = false;
            state.error = extractError(action);
        });

        builder.addCase(deleteRankSet.pending, (state) => {
            state.isDeleteRankSetPending = true;
            state.error = null;
        });
        builder.addCase(deleteRankSet.fulfilled, (state, action) => {
            state.isDeleteRankSetPending = false;
            const removedId = action.payload;
            state.rankSets = (state.rankSets ?? []).filter((x) => x.id !== removedId);
        });
        builder.addCase(deleteRankSet.rejected, (state, action) => {
            state.isDeleteRankSetPending = false;
            state.error = extractError(action);
        });

        builder.addCase(duplicateRankSet.pending, (state) => {
            state.isDuplicateRankSetPending = true;
            state.error = null;
        });
        builder.addCase(duplicateRankSet.fulfilled, (state, action) => {
            state.isDuplicateRankSetPending = false;
            const createdRankSet = action.payload;
            state.rankSets = [...(state.rankSets ?? []), createdRankSet];
        });
        builder.addCase(duplicateRankSet.rejected, (state, action) => {
            state.isDuplicateRankSetPending = false;
            state.error = extractError(action);
        });

        builder.addCase(toggleActiveRankReference.pending, (state) => {
            state.isToggleActiveRankReferencePending = true;
            state.error = null;
        });
        builder.addCase(toggleActiveRankReference.fulfilled, (state, action) => {
            state.isToggleActiveRankReferencePending = false;
            state.ranks = action.payload?.data ?? state.ranks;
        });
        builder.addCase(toggleActiveRankReference.rejected, (state, action) => {
            state.isToggleActiveRankReferencePending = false;
            state.error = extractError(action);
        });

        builder.addCase(getAdminCenterData.fulfilled, (state, action) => {
            const {
                vesselDepartmentResponse,
                proficiencyLevelResponse,
                rankShortCodeResponse,
                rankSetResponse,
                rankSetWithReference,
            } = action.payload;

            if (vesselDepartmentResponse) {
                state.vesselDepartments = vesselDepartmentResponse.data ?? [];
            }

            if (proficiencyLevelResponse) {
                state.proficiencyLevels = proficiencyLevelResponse.data ?? [];
            }

            if (rankShortCodeResponse) {
                state.rankShortCodes = rankShortCodeResponse ?? [];
            }

            if (rankSetResponse) {
                state.rankSets = rankSetResponse ?? [];
            }

            if (rankSetWithReference?.length) {
                const selectedRankSetId = Number(state.selectedRankSet?.value);
                const currentRankSet =
                    rankSetWithReference.find((item) => item.rankSetId === selectedRankSetId) ??
                    rankSetWithReference[0];

                const rankReference = currentRankSet?.rankReference;
                const data = rankReference?.data ?? [];

                state.ranks = data;
                state.commonRanks = rankReference ?? null;

                state.transformedRanksForMatrices = data
                    .filter((rank) => rank.vesselDepartmentId !== null && rank.proficiencyLevelId !== null)
                    .filter((item) => item.isActive)
                    .map((rank) => ({
                        value: String(rank.id),
                        label: rank.tenantShortCode,
                        type: String(rank.vesselDepartmentId),
                        category: String(rank.proficiencyLevelId),
                        itemOrder: rank.itemOrder,
                    }));
            }
        });
    },
});

export const {
    setIsAddNewRankSetModalOpened,
    setIsDeleteRankSetModalOpened,
    setIsCopyRankSetModalOpened,
    setSelectedRankSet,
    setRecordsRowOrder,
    resetRecordsRowOrder,
} = rankReferenceSlice.actions;
export default rankReferenceSlice.reducer;
